import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/http-api';
import { Effect, Schema } from 'effect';
import { InternalServerError } from './errors';

// PostHog feature flags are always boolean (toggle) or string (multivariate).
// posthog-node's getAllFlags returns Record<string, string | boolean>.
const FeatureFlagValue = Schema.Union([Schema.String, Schema.Boolean]);

export const BootstrapResponse = Schema.Struct({
  distinctId: Schema.String,
  /**
   * Raw PostHog values. Web uses them to bootstrap posthog-js; mobile app
   * versions released before `Flags.Get` read their `enable-*` flags here.
   * New code reads flags from `Flags.Get` instead.
   */
  featureFlags: Schema.Record(Schema.String, FeatureFlagValue),
  isIdentifiedID: Schema.Boolean,
  /**
   * True when the API minted a fresh `distinctId` because no PostHog cookie
   * was present on the request. The lambda must emit a Set-Cookie on the
   * browser response (using the canonical `ph_<sanitized-apikey>_posthog`
   * name + JSON payload) so subsequent page loads reuse the same identity.
   * Otherwise every page load mints a new anonymous id and PostHog never
   * builds session continuity for anonymous users.
   *
   * Optional with a `false` default so older API deployments (e.g. the
   * development stage before this PR's API change lands) parse cleanly —
   * PR previews talk to the dev API, and a missing field would otherwise
   * fail SSR Analytics.Bootstrap with `ParseError: ["newDistinctIdGenerated"]
   * is missing`. Tighten back to `Schema.Boolean` once the field is
   * deployed everywhere.
   */
  newDistinctIdGenerated: Schema.Boolean.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(false)),
  ),
});

const bootstrapEndpoint = HttpApiEndpoint.get('Bootstrap', '/bootstrap', {
  success: BootstrapResponse.pipe(HttpApiSchema.status(200)),
  error: InternalServerError,
})
  .annotate(OpenApi.Summary, 'Analytics Bootstrap')
  .annotate(
    OpenApi.Description,
    'Evaluate PostHog feature flags server-side for SSR bootstrap. ' +
      'Reads the PostHog cookie from the forwarded cookie header to identify the user.',
  );

export const analyticsGroup = HttpApiGroup.make('Analytics')
  .add(bootstrapEndpoint)
  .prefix('/analytics');
